<template>
    <div class="min-h-screen bg-navy flex items-center justify-center p-4">
      <NuxtLink 
        to="/login" 
        class="absolute top-4 left-4 text-gray-400 hover:text-white flex items-center gap-2 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back to Login
      </NuxtLink>
  
      <div class="max-w-md w-full bg-navy-800 rounded-lg p-8 shadow-xl">
        <!-- Error Alert -->
        <div v-if="error" class="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
          {{ error }}
        </div>
        
        <!-- Success Alert -->
        <div v-if="success" class="mb-6 p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-200 text-sm">
          {{ success }}
        </div>
  
        <!-- Title -->
        <h1 class="text-white text-2xl font-bold text-center mb-2">Forgot Password</h1>
        <p class="text-gray-400 text-center mb-8">
          Enter your email address, and we'll send you a link to reset your password.
        </p>
  
        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-6" v-if="!success">
          <!-- Email Input -->
          <div>
            <label class="block text-gray-300 text-sm font-medium mb-2">Email</label>
            <input 
              type="email"
              v-model="email"
              :disabled="isLoading"
              class="w-full bg-navy-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 disabled:opacity-50"
              placeholder="Enter your email"
            />
          </div>
  
          <!-- Submit Button -->
          <button 
            type="submit"
            :disabled="isLoading"
            class="w-full bg-cyan-500/50 hover:bg-cyan-400/50 border border-cyan-500 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors backdrop-blur-sm text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isLoading">Sending...</span>
            <span v-else>Send Reset Link</span>
          </button>
        </form>
  
        <!-- Links -->
        <div v-if="success" class="mt-6 text-center">
          <NuxtLink to="/login" class="text-cyan-400 hover:text-cyan-300">
            Back to Login
          </NuxtLink>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref } from 'vue'
  import { useAuth } from '~/composables/useAuth'
  
  const { sendPasswordResetEmail } = useAuth()
  
  const email = ref('')
  const isLoading = ref(false)
  const error = ref('')
  const success = ref('')
  
  const handleSubmit = async () => {
    if (!email.value) {
      error.value = 'Please enter your email address'
      return
    }
  
    try {
      isLoading.value = true
      error.value = ''
      await sendPasswordResetEmail(email.value)
      success.value = 'Password reset instructions have been sent to your email'
      email.value = '' // Clear the form
    } catch (err) {
      console.error('Password reset error:', err)
      error.value = err.message || 'Failed to send password reset email'
    } finally {
      isLoading.value = false
    }
  }
  </script>